import {
  Backdrop,
  Box,
  CircularProgress,
  Paper,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from "@mui/material";
import PropTypes from "prop-types";

export default function SyncWizard({
  steps,
  activeStepIndex,
  onStepClick,
  isLoading = false,
  loadingMessage = "Loading...",
  loadingBackdropTestId = "sync-wizard-backdrop",
  children = null,
  disableStepClick = false,
  hideStepLabels = false,
  alternativeLabel = false,
  maxWidth = 800,
  navMaxWidth = 800,
}) {
  SyncWizard.propTypes = {
    steps: PropTypes.arrayOf(PropTypes.object).isRequired,
    activeStepIndex: PropTypes.number.isRequired,
    onStepClick: PropTypes.func,
    isLoading: PropTypes.bool,
    loadingMessage: PropTypes.string,
    loadingBackdropTestId: PropTypes.string,
    children: PropTypes.node,
    disableStepClick: PropTypes.bool,
    hideStepLabels: PropTypes.bool,
    alternativeLabel: PropTypes.bool,
    maxWidth: PropTypes.number,
    // Width of the step navigation (defaults to 800 to match existing wizards).
    // Widen it when there are many steps so labels have room on one line.
    navMaxWidth: PropTypes.number,
  };

  if (!children && !steps.every(({ content }) => content)) {
    throw new Error(
      "SyncWizard must have either children or steps with content",
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
      {/* Navigation stays at consistent width */}
      <Box sx={{ maxWidth: navMaxWidth, width: "100%", mb: 2 }}>
        <Stepper activeStep={activeStepIndex} alternativeLabel={alternativeLabel}>
          {steps.map(({ label }, idx) => (
            <Step 
              key={label}
              sx={{ 
                cursor: onStepClick && !disableStepClick ? 'pointer' : 'default',
                '&:hover': {
                  opacity: onStepClick && !disableStepClick ? 0.8 : 1
                }
              }}
              onClick={() => onStepClick && !disableStepClick && onStepClick(idx)}
            >
              <StepLabel
                StepIconProps={{
                  "data-testid": `step-icon-${idx + 1}`,
                }}
              >
                {hideStepLabels ? '' : label}
              </StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>
      
      {/* Content area uses dynamic width */}
      <Box sx={{ maxWidth: maxWidth, width: "100%" }}>
        <Paper
          variant="outlined"
          square={true}
          sx={{ p: 3, position: "relative" }}
        >
          <Backdrop
            data-testid={loadingBackdropTestId}
            sx={{
              color: "primary.light",
              position: "absolute",
              display: "flex",
              flexDirection: "column",
              zIndex: (theme) => theme.zIndex.drawer + 1,
            }}
            open={isLoading}
          >
            <CircularProgress sx={{ mb: 2, color: "inherit" }} />
            <Typography variant="h5" sx={{ color: "inherit" }}>
              {loadingMessage}
            </Typography>
          </Backdrop>
          {children && children}
          {!children && steps[activeStepIndex].content}
        </Paper>
      </Box>
    </Box>
  );
}
